import React, { useState } from "react";
import styled from "styled-components";
import { useRequest } from "../../hooks/useRequest";
import { loginUserObj } from "../../utils/constants";
import { toast } from "react-toastify";
import Progress from "../../components/common/Progress";
import { useAppContext } from "../../context/useAppContext";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import SEO from "../../components/seo/SEO";

const ComingSoonContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    opacity: 0.3;
  }
`;

const HeroSection = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  position: relative;
  z-index: 1;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 40px;
    text-align: center;
  }
`;

const TextSection = styled.div`
  color: white;
  h1 {
    font-size: 72px;
    font-weight: 700;
    margin-bottom: 24px;
    line-height: 1.1;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);

    @media (max-width: 768px) {
      font-size: 48px;
    }

    @media (max-width: 480px) {
      font-size: 36px;
    }
  }

  p {
    font-size: 20px;
    line-height: 1.6;
    margin-bottom: 16px;
    opacity: 0.95;

    @media (max-width: 768px) {
      font-size: 18px;
    }
  }

  .subtitle {
    font-size: 16px;
    opacity: 0.9;
    margin-top: 8px;
  }
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 450px;
  width: 100%;
  margin: 0 auto;

  @media (max-width: 480px) {
    padding: 30px 20px;
  }

  .login__title {
    color: #000;
    text-align: center;
    font-size: 28px;
    font-weight: 600;
    margin-bottom: 8px;
  }

  .login__subtitle {
    color: #666;
    text-align: center;
    font-size: 14px;
    font-weight: 400;
    margin-bottom: 32px;
  }

  .login__form {
    .form__group {
      margin-bottom: 24px;
      label {
        color: #464646;
        font-size: 14px;
        font-weight: 500;
        display: block;
        margin-bottom: 8px;
      }
      input {
        border-radius: 8px;
        border: 1px solid #d9d9d9;
        background: #fff;
        width: 100%;
        height: 48px;
        padding: 0 16px;
        font-size: 14px;
        transition: all 0.3s ease;

        &:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        &.error {
          border-color: #ff0000;
        }
      }
    }

    .password__wrapper {
      position: relative;
      input {
        padding-right: 45px;
      }
      .password__toggle {
        position: absolute;
        right: 16px;
        top: 50%;
        transform: translateY(-50%);
        cursor: pointer;
        color: #999;
        display: flex;
        align-items: center;
        transition: color 0.2s;

        &:hover {
          color: #667eea;
        }
      }
    }

    .error__message {
      color: #ff0000;
      font-size: 12px;
      margin-top: 6px;
    }

    .login__button {
      width: 100%;
      height: 50px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border: none;
      border-radius: 8px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
      margin-top: 8px;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
      }

      &:active {
        transform: translateY(0);
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }
  }
`;

const ComingSoon = () => {
    const [userInfo, setUserInfo] = useState(loginUserObj);
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState({
        username: false,
        password: false,
    });
    const [login, { isLoading }] = useRequest();
    const { setUser } = useAppContext();
    const navigate = useNavigate();

    const handleSignIn = async (e) => {
        e.preventDefault();

        // Reset errors
        setError({ username: false, password: false });

        // Validate
        if (!userInfo.username || !userInfo.password) {
            if (!userInfo.username) {
                setError((prev) => ({ ...prev, username: true }));
            }
            if (!userInfo.password) {
                setError((prev) => ({ ...prev, password: true }));
            }
            return;
        }

        const path = `/auth/login`;
        const response = await login({
            path,
            method: "POST",
            body: JSON.stringify(userInfo),
        });

        if (!response.success) {
            return toast.error(response.message || "Login failed. Please check your credentials.");
        }

        toast.success(response.message || "Login successful!");
        setUser(response.data);
        const now = new Date();
        const expirationDate = new Date(now.getTime() + 24 * 60 * 60 * 1000);
        localStorage.setItem("expirationDate", expirationDate.toISOString());
        navigate("/");
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUserInfo((prev) => ({
            ...prev,
            [name]: value,
        }));
        setError((prev) => ({
            ...prev,
            [name]: false,
        }));
    };

    return (
        <>
            <SEO title="Coming Soon - Instabuild" />
            <ComingSoonContainer>
                <HeroSection>
                    <ContentWrapper>
                        <TextSection>
                            <h1>Coming Soon</h1>
                            <p>
                                We're working hard to bring you something amazing. Stay tuned for our grand launch!
                            </p>
                            <p className="subtitle">
                                Already have an account? Sign in below to access your dashboard.
                            </p>
                        </TextSection>

                        <LoginCard>
                            <h2 className="login__title">Welcome Back</h2>
                            <p className="login__subtitle">Sign in to your account</p>

                            <form onSubmit={handleSignIn} className="login__form">
                                <div className="form__group">
                                    <label htmlFor="email">Email Address</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="username"
                                        value={userInfo?.username || ""}
                                        className={error?.username ? "error" : ""}
                                        onChange={handleChange}
                                        placeholder="Enter your email"
                                    />
                                    {error?.username && (
                                        <div className="error__message">Email is required.</div>
                                    )}
                                </div>

                                <div className="form__group">
                                    <label htmlFor="password">Password</label>
                                    <div className="password__wrapper">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            id="password"
                                            name="password"
                                            value={userInfo?.password || ""}
                                            className={error?.password ? "error" : ""}
                                            onChange={handleChange}
                                            placeholder="Enter your password"
                                        />
                                        <span
                                            className="password__toggle"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                                        </span>
                                    </div>
                                    {error?.password && (
                                        <div className="error__message">Password is required.</div>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    className="login__button"
                                    disabled={isLoading}
                                >
                                    {isLoading ? "Signing In..." : "Sign In"}
                                </button>
                            </form>
                        </LoginCard>
                    </ContentWrapper>
                </HeroSection>
                {isLoading && <Progress />}
            </ComingSoonContainer>
        </>
    );
};

export default ComingSoon;

